import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { GreaterThanZeroNumberValidator } from './generic.validation';

export function LessOrEqual(comparedControlPath: string, errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control.root) {
      return null;
    }

    const rootControl = control.root;

    const comparedControl = rootControl.get(comparedControlPath);
    const comparedValue = +comparedControl?.value || 0;
    const value = +control.value;

    if (isNaN(value) || !(value <= comparedValue)) {
      return { lessOrEqual: true, errorMessage };
    }

    return null;
  };
}

export function GreaterOrEqual(comparedControlPath: string, errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control.root) {
      return null;
    }

    const rootControl = control.root;

    const comparedControl = rootControl.get(comparedControlPath);
    if (!comparedControl) {
      return null;
    }    
    const comparedValue = +comparedControl.value;
    const value = +control.value;

    if (isNaN(value) || !(value >= comparedValue)) {
      return { greaterOrEqual: true, errorMessage };
    }

    return null;
  };
}

export function SameProvince(province: string, errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value?.toLowerCase() !== province?.toLowerCase()) {
      return { notSameProvince: true, errorMessage };
    }

    return null;
  };
}

export function SameValue(comparedControlPath: string, errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control.root) {
      return null;
    }

    const rootControl = control.root;
    const comparedControl = rootControl.get(comparedControlPath);

    if (control.value !== comparedControl?.value) {
      return { sameValue: true, errorMessage };
    }

    return null;
  };
}

export function Pattern(pattern: string | RegExp, errorMessage: string): ValidatorFn {
  const patternValidatorFn = Validators.pattern(pattern);

  return (control: AbstractControl) => (patternValidatorFn(control) === null ? null : { pattern: true, errorMessage });
}

export function MaxValue(maxValue: number, errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = +control.value;

    if (isNaN(value) || !(value <= maxValue)) {
      return { maxValue: true, errorMessage };
    }

    return null;
  };
}

export function LengthValidation(length: number = 10, errorMessage: string = 'errors.phoneLength'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value && value.length !== length) {
      return { length: true, errorMessage };
    }

    return null;
  };
}

export function MinLengthValidation(length: number = 10, errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value && value.length < length) {
      return { length: true, errorMessage };
    }

    return null;
  };
}

export function MaxLengthValidation(length: number = 15, errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value && value.length >= length) {
      return { length: true, errorMessage };
    }

    return null;
  };
}

export function DateRangeValue(range: number[], errorMessage: string, minValue?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value && !control.value?.formatted) {
      return null;
    }
    const selectedDate = new Date(control.value.formatted);

    const fixedMinDate = minValue ? new Date(minValue) : null;
    const dynamicMinDate = new Date();
    dynamicMinDate.setDate(dynamicMinDate.getDate() - range[0]);
    const minDate = !fixedMinDate || dynamicMinDate > fixedMinDate ? dynamicMinDate : fixedMinDate;
    minDate.setHours(0, 0, 0, 0);

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + range[1]);
    maxDate.setHours(0, 0, 0, 0);

    if (selectedDate < minDate || selectedDate > maxDate) {
      return { dateRangeValue: true, errorMessage };
    }

    return null;
  };
}

export function GreaterThanZero(): ValidatorFn {
  return GreaterThanZeroNumberValidator;
}
